/**
 * Filters for the AI cost dashboard. Task, course, model and learner narrow every number on the
 * page; status and search narrow only the call list (see pageFilters). No server imports: the
 * client parses with this too.
 */

export const COST_PERIODS = ["24h", "7d", "30d", "90d"] as const;
export type CostPeriod = (typeof COST_PERIODS)[number];

export function isCostPeriod(value: unknown): value is CostPeriod {
  return typeof value === "string" && (COST_PERIODS as readonly string[]).includes(value);
}

/**
 * The dates the dashboard covers: a preset ending now, or a custom span of Pakistan calendar
 * dates ("2026-10-01" to "2026-10-15", both days included).
 */
export type CostRange = { period: CostPeriod } | { from: string; to: string };

/** The longest custom span, in days. */
export const MAX_CUSTOM_DAYS = 366;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** A Pakistan calendar date ("2026-10-01") as the instant it starts; null when it isn't a real date. */
export function pakistanDayStart(date: string): Date | null {
  if (!ISO_DATE.test(date)) return null;
  const d = new Date(`${date}T00:00:00+05:00`);
  // Rejects dates that roll over, like 2026-02-30.
  return Number.isNaN(d.getTime()) || new Date(d.getTime() + 5 * 60 * 60 * 1000).toISOString().slice(0, 10) !== date ? null : d;
}

/** How many calendar days a custom range covers, both ends included. */
export function customRangeDays(from: string, to: string): number | null {
  const a = pakistanDayStart(from);
  const b = pakistanDayStart(to);
  return a && b ? Math.round((b.getTime() - a.getTime()) / DAY_MS) + 1 : null;
}

/** Reads `period`, or `from` and `to`, from query parameters. Defaults to the last 30 days. */
export function parseCostRange(params: URLSearchParams): { range: CostRange; error: string | null } {
  const from = params.get("from")?.trim();
  const to = params.get("to")?.trim();
  if (from || to) {
    if (!from || !to) return { range: { period: "30d" }, error: "from and to must be given together" };
    const days = customRangeDays(from, to);
    if (days === null) return { range: { period: "30d" }, error: "from and to must be dates like 2026-10-01" };
    if (days < 1) return { range: { period: "30d" }, error: "from must not be after to" };
    if (days > MAX_CUSTOM_DAYS) return { range: { period: "30d" }, error: `a custom range can cover at most ${MAX_CUSTOM_DAYS} days` };
    return { range: { from, to }, error: null };
  }
  const period = params.get("period")?.trim() || "30d";
  if (!isCostPeriod(period)) return { range: { period: "30d" }, error: "period must be 24h, 7d, 30d or 90d" };
  return { range: { period }, error: null };
}

/** The range as query parameters. */
export function costRangeParams(range: CostRange): [string, string][] {
  return "period" in range ? [["period", range.period]] : [["from", range.from], ["to", range.to]];
}

/** The cost dashboard reports in Pakistan time, wherever the viewer is. */
export const REPORT_TIME_ZONE = "Asia/Karachi";

/** How long Langfuse keeps a call's prompt and reply on our plan. */
export const PROMPT_RETENTION_DAYS = 30;

/** `llm_calls.status`, as inara's LLM client writes it. */
export const CALL_STATUSES = ["ok", "error", "empty", "truncated"] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];

export const STATUS_LABELS: Record<CallStatus, string> = {
  ok: "OK",
  error: "Failed",
  empty: "No reply",
  truncated: "Reply cut off",
};

/** The status filter: one status, or every call that isn't OK. */
export const STATUS_FILTERS = ["problem", ...CALL_STATUSES] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];
export const STATUS_FILTER_LABELS: Record<StatusFilter, string> = { problem: "Any problem", ...STATUS_LABELS };

/** Stands for a NULL task or course, in a filter or a breakdown row. */
export const NONE_KEY = "none";

export type CostFilters = {
  /** `llm_task_settings.task`, or NONE_KEY for calls with no task. */
  task?: string;
  /** A course id, or NONE_KEY for calls with no course. */
  course?: number | typeof NONE_KEY;
  /** An organization id, or NONE_KEY for calls with no organization (Direct/Non-B2B). */
  organization?: number | typeof NONE_KEY;
  model?: string;
  /**
   * A learner's Clerk id (`llm_calls.triggered_by`). Only a learner's own lesson opens record one;
   * work started from the admin side records nobody, so an admin who also studies shows only
   * their cost as a student.
   */
  learner?: string;
  status?: StatusFilter;
  /** A lesson id, a learner's email or id, a job id or a generation id; see parseSearch. */
  search?: string;
};

/** The filters that narrow the whole page (totals, chart, breakdowns), not just the call list. */
export function pageFilters(filters: CostFilters): CostFilters {
  return { task: filters.task, course: filters.course, organization: filters.organization, model: filters.model, learner: filters.learner };
}

const CLERK_ID = /^user_[A-Za-z0-9]+$/;

export type ParsedSearch =
  | { kind: "lesson"; value: number }
  | { kind: "email"; value: string }
  | { kind: "job"; value: string }
  | { kind: "learner"; value: string }
  | { kind: "generation"; value: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * What a search box value means: digits are a lesson number (`lessons.id`), an email or `user_…`
 * is a learner, a UUID is the background job id and `gen-…` is the OpenRouter id of one call.
 * Anything else is null.
 */
export function parseSearch(raw: string): ParsedSearch | null {
  const value = raw.trim();
  if (/^\d{1,9}$/.test(value)) return { kind: "lesson", value: Number(value) };
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return { kind: "email", value: value.toLowerCase() };
  if (UUID.test(value)) return { kind: "job", value: value.toLowerCase() };
  if (CLERK_ID.test(value)) return { kind: "learner", value };
  if (/^gen-[A-Za-z0-9-]+$/.test(value)) return { kind: "generation", value };
  return null;
}

export const SEARCH_HELP =
  "Enter a lesson number (like 191), a learner's email or id (starts with user_), a background job id, or an OpenRouter id (starts with gen-).";

/**
 * Reads filters from query parameters. Every valid one is kept (the page reads its URL with
 * this, and one bad value shouldn't drop the rest); `error` names the first one that isn't.
 */
export function parseCostFilters(params: URLSearchParams): { filters: CostFilters; error: string | null } {
  const filters: CostFilters = {};
  let error: string | null = null;
  const fail = (message: string) => {
    error ??= message;
  };
  const task = params.get("task")?.trim();
  if (task) filters.task = task;
  const course = params.get("course")?.trim();
  if (course) {
    if (course === NONE_KEY || /^\d{1,9}$/.test(course)) filters.course = course === NONE_KEY ? NONE_KEY : Number(course);
    else fail("course must be a course id");
  }
  const organization = params.get("organization")?.trim();
  if (organization) {
    if (organization === NONE_KEY || /^\d{1,9}$/.test(organization)) filters.organization = organization === NONE_KEY ? NONE_KEY : Number(organization);
    else fail("organization must be an organization id");
  }
  const model = params.get("model")?.trim();
  if (model) filters.model = model;
  const learner = params.get("learner")?.trim();
  if (learner) {
    if (CLERK_ID.test(learner)) filters.learner = learner;
    else fail("learner must be a learner id (user_…)");
  }
  const status = params.get("status")?.trim();
  if (status) {
    if ((STATUS_FILTERS as readonly string[]).includes(status)) filters.status = status as StatusFilter;
    else fail(`status must be one of ${STATUS_FILTERS.join(", ")}`);
  }
  const search = params.get("search")?.trim();
  if (search) {
    if (parseSearch(search)) filters.search = search;
    else fail(SEARCH_HELP);
  }
  return { filters, error };
}

/** The same filters as query parameters, for the client to send. */
export function costFilterParams(filters: CostFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.task) params.set("task", filters.task);
  if (filters.course !== undefined) params.set("course", String(filters.course));
  if (filters.organization !== undefined) params.set("organization", String(filters.organization));
  if (filters.model) params.set("model", filters.model);
  if (filters.learner) params.set("learner", filters.learner);
  if (filters.status) params.set("status", filters.status);
  if (filters.search) params.set("search", filters.search);
  return params;
}

export function hasCostFilters(filters: CostFilters): boolean {
  return [...costFilterParams(filters).keys()].length > 0;
}

/** What the learner cost table can be sorted by. */
export const LEARNER_SORTS = ["cost", "calls", "tokensIn", "tokensOut", "courses", "lastAt"] as const;
export type LearnerSort = (typeof LEARNER_SORTS)[number];

export const LEARNERS_PAGE_SIZE = 25;
const MAX_LEARNER_PAGE = 10_000;
const MAX_LEARNER_SEARCH = 100;

/** The learner cost table's own filters, sort and page. */
export type LearnerQuery = {
  /** A course id, or NONE_KEY for calls with no course. */
  course?: number | typeof NONE_KEY;
  /** Part of a learner's email, or their whole Clerk id. */
  search?: string;
  sort: LearnerSort;
  dir: "asc" | "desc";
  /** From 1. */
  page: number;
};

export const DEFAULT_LEARNER_QUERY: LearnerQuery = { sort: "cost", dir: "desc", page: 1 };

/** Reads the learner table's query parameters; `error` names the first bad one, the rest keep their defaults. */
export function parseLearnerQuery(params: URLSearchParams): { query: LearnerQuery; error: string | null } {
  const query: LearnerQuery = { ...DEFAULT_LEARNER_QUERY };
  let error: string | null = null;
  const fail = (message: string) => {
    error ??= message;
  };
  const course = params.get("course")?.trim();
  if (course) {
    if (course === NONE_KEY || /^\d{1,9}$/.test(course)) query.course = course === NONE_KEY ? NONE_KEY : Number(course);
    else fail("course must be a course id");
  }
  const search = params.get("q")?.trim();
  if (search) {
    if (search.length <= MAX_LEARNER_SEARCH) query.search = search;
    else fail(`q must be at most ${MAX_LEARNER_SEARCH} characters`);
  }
  const sort = params.get("sort")?.trim();
  if (sort) {
    if ((LEARNER_SORTS as readonly string[]).includes(sort)) query.sort = sort as LearnerSort;
    else fail(`sort must be one of ${LEARNER_SORTS.join(", ")}`);
  }
  const dir = params.get("dir")?.trim();
  if (dir) {
    if (dir === "asc" || dir === "desc") query.dir = dir;
    else fail("dir must be asc or desc");
  }
  const page = params.get("page")?.trim();
  if (page) {
    if (/^\d{1,5}$/.test(page) && Number(page) >= 1 && Number(page) <= MAX_LEARNER_PAGE) query.page = Number(page);
    else fail("page must be a page number");
  }
  return { query, error };
}

/** The learner table's query as parameters, leaving out the defaults. */
export function learnerQueryParams(query: LearnerQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.course !== undefined) params.set("course", String(query.course));
  if (query.search) params.set("q", query.search);
  if (query.sort !== DEFAULT_LEARNER_QUERY.sort) params.set("sort", query.sort);
  if (query.dir !== DEFAULT_LEARNER_QUERY.dir) params.set("dir", query.dir);
  if (query.page !== 1) params.set("page", String(query.page));
  return params;
}
